import "./consigneeManagement.css";
import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Table, Input, Button } from "antd";
import { FormInstance } from "antd";
import { getConsigneeList } from "@/services/api/manualOrder";
import AddOrUpdateConsigneeForm, { ConsigneeFormValue } from "./AddConsigneeForm";
import type { TableColumnsType, TablePaginationConfig } from "antd";
import DeleteConsigneeButton from "./DeleteConsigneeButton";
import ManualOrderConsigneeDTO from "@/dto/manualOrder/ManualOrderConsigneeDTO";

interface ConsigneeManagementFormProps {
    shopId: number;
}

type SearchType = {
    _value: string;
    _pageNo: number;
};

const PAGE_SIZE = 10;

interface UseColumnsProps {
    shopId: number;

    onEdit?(data: ManualOrderConsigneeDTO): void;

    onDelete?(data: ManualOrderConsigneeDTO): void;
}

// ❗❗❗ column必须有一row引用dataIndex才会重新触发Table的渲染
function useColumns(props: UseColumnsProps): TableColumnsType<ManualOrderConsigneeDTO> {
    const { shopId, onDelete, onEdit } = props;

    const [columns, setColumns] = useState<TableColumnsType<ManualOrderConsigneeDTO>>();

    useEffect(() => {
        setColumns([
            {
                title: "常用收货人信息",
                key: "consignee",
                dataIndex: "name",
                render: (name, allData) => {
                    const { province, city, district, mobile, telephone, street } = allData;

                    return (
                        <span data-openreplay-masked>
                            {name}，{mobile || telephone}，{province}
                            {city}
                            {district}
                            {street}
                        </span>
                    );
                },
            },
            {
                title: "",
                key: "operation",
                render: (_, allData) => (
                    <div className="tr">
                        <Button
                            type="link"
                            size="small"
                            className="mr10"
                            onClick={() => {
                                onEdit?.(allData);
                            }}
                        >
                            编辑
                        </Button>

                        <DeleteConsigneeButton value={allData} shopId={props.shopId} onSuccess={onDelete} />
                    </div>
                ),
            },
        ]);
    }, [shopId, onDelete, onEdit]);

    return columns;
}

const ConsigneeManagementForm: React.FC<ConsigneeManagementFormProps> = (props) => {
    const { shopId } = props;
    const [consigneeName, setConsigneeName] = useState<string>("");
    const [pageNo, setPageNo] = useState<number>(0);
    const [totalCount, setTotalCount] = useState<number>(null);
    const [dataSource, setDataSource] = useState<ManualOrderConsigneeDTO[]>([]);
    const [showEditor, setShowEditor] = useState<boolean>(false);
    const formRef = useRef<FormInstance<ConsigneeFormValue>>();

    // ----------------------------------lifecycle---------------------------------------
    useEffect(() => {
        handleSearch({ _value: consigneeName, _pageNo: pageNo });
    }, []);

    //----------------------------------method and computed-------------------------------
    const handleConsigneeNameChange = useCallback((e: any) => {
        setConsigneeName(e.target.value);
    }, []);

    const handleSearch = useCallback(
        (value?: SearchType) => {
            const { shopId } = props;
            const search = async () => {
                const { _value, _pageNo } = value;
                const consigneeList = await getConsigneeList({
                    consigneeName: _value,
                    pageNo: _pageNo,
                    pageSize: PAGE_SIZE,
                    shopId,
                });
                setPageNo(_pageNo);
                setDataSource(consigneeList.items);
                setTotalCount(consigneeList.totalCount);
            };

            search();
        },
        [props.shopId, consigneeName, pageNo, PAGE_SIZE],
    );

    const handlePageNoChange = useCallback(
        (pagination: TablePaginationConfig) => {
            const newPageNo = pagination.current - 1;

            setPageNo(newPageNo);

            handleSearch({ _value: consigneeName, _pageNo: newPageNo });
        },
        [handleSearch, pageNo, consigneeName],
    );

    // 显示收货人编辑器
    const handlesShowAddConsigneeEditor = useCallback(() => {
        formRef.current.resetFields();

        setShowEditor(true);
    }, []);

    const handleCancelEditConsignee = useCallback(() => {
        formRef.current.resetFields();

        setShowEditor(false);
    }, []);

    // 新增更新收货人信息成功回调
    const onAddOrUpdateSuccess = useCallback(() => {
        handleSearch({ _value: consigneeName, _pageNo: pageNo });

        handleCancelEditConsignee();
    }, [consigneeName, pageNo]);

    // 编辑收货人
    const onEdit = useCallback((data: ManualOrderConsigneeDTO) => {
        setShowEditor(true);

        const { provinceCode, cityCode, districtCode } = data;

        const area = [provinceCode, cityCode, districtCode];

        formRef.current.setFieldsValue({
            ...data,
            area,
        });
    }, []);

    // 删除收货人
    const onDelete = useCallback((data: ManualOrderConsigneeDTO) => {
        setDataSource((currentDataSource) => currentDataSource.filter((consignee) => data.id !== consignee.id));
    }, []);

    const columns = useColumns({
        shopId,
        onEdit,
        onDelete,
    });

    const current = useMemo(() => pageNo + 1, [pageNo]);
    //----------------------------------render--------------------------------

    return (
        <div className="frequent-consignee-dialog">
            <div className="frequent-consignee-table-container">
                <div className="mb10">
                    <Input.Search
                        placeholder="输入收货人姓名搜索..."
                        value={consigneeName}
                        onChange={handleConsigneeNameChange}
                        onSearch={(val) => {
                            handleSearch({ _value: val, _pageNo: 0 });
                        }}
                        enterButton
                        style={{ width: 300 }}
                        className="mr10"
                    />

                    <Button type="primary" onClick={handlesShowAddConsigneeEditor}>
                        添加常用收货人
                    </Button>
                </div>

                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={dataSource}
                    pagination={{
                        showSizeChanger: false,
                        showQuickJumper: false,
                        total: totalCount,
                        pageSize: PAGE_SIZE,
                        current,
                    }}
                    onChange={handlePageNoChange}
                />
            </div>

            <div className="frequent-consignee-editor-container" style={{ display: showEditor ? "block" : "none" }}>
                <AddOrUpdateConsigneeForm
                    ref={formRef}
                    shopId={shopId}
                    onSuccess={onAddOrUpdateSuccess}
                    onCancel={handleCancelEditConsignee}
                />
            </div>
        </div>
    );
};

export default ConsigneeManagementForm;
